import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { http, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const INITIAL = [
  { role: "assistant", content: "Hi! I'm Clara, your QuadScrub concierge. Ask me about plans, pricing, or which package fits your student's place." },
];

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(INITIAL);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  async function send() {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await http.post("/ai/chat", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Sorry — ${formatError(e.response?.data?.detail) || "I had trouble responding. Try again."}` }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-toggle"
        className="fixed bottom-20 right-6 z-[9999] w-14 h-14 rounded-full bg-secondary text-secondary-foreground shadow-lg shadow-secondary/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        aria-label="Open assistant"
      >
        {open ? <X /> : <MessageCircle />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[380px] h-[520px] bg-background border border-border rounded-2xl ring-soft flex flex-col overflow-hidden" data-testid="ai-chat-panel">
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><Sparkles size={16} /></div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-sm">Clara · AI Concierge</div>
              <div className="text-[10px] text-white/70">Plans, pricing & quick answers</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-sm whitespace-pre-wrap rounded-2xl px-3 py-2 ${
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-background border border-border/60"
                }`}>{m.content}</div>
              </div>
            ))}
            {busy && (
              <div className="flex"><div className="bg-background border border-border/60 rounded-2xl px-3 py-2 text-xs text-muted-foreground">thinking…</div></div>
            )}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-border bg-background flex gap-2"
          >
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about plans, pricing, schedule…"
              className="flex-1 text-sm px-3 py-2 rounded-lg bg-muted/50 border border-border/60 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <Button type="submit" size="icon" className="bg-secondary hover:bg-secondary/90" data-testid="ai-chat-send" disabled={busy}>
              <Send size={16} />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
